import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, User } from '../../types';
import { authService, ApiResponse } from '../../services/api';

// Estado inicial
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
  
  // Estados para OTP
  requireOtp: false,
  requireTrustDevice: false,
  currentDeviceId: null,
  otpVerified: false
};

// Acciones asíncronas
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Error al obtener el perfil del usuario');
      }
      
      return response.data;
    } catch (error: any) {
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(
        credentials.mobile_phone, 
        credentials.password,
        credentials.is_distributor
      );
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Error de autenticación');
      }
      
      // Guardar token en localStorage
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error: any) {
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async ({ deviceId, deviceName }: { deviceId: string, deviceName: string }, { rejectWithValue }) => {
    try {
      const response = await authService.sendOtp(deviceId, deviceName);
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Error al enviar OTP');
      }
      
      return {
        requireOtp: response.data?.require_validate_otp || false,
        requireTrustDevice: response.data?.require_trust_device || false,
        currentDeviceId: deviceId
      };
    } catch (error: any) {
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const validateOtp = createAsyncThunk(
  'auth/validateOtp',
  async ({ otp, deviceId }: { otp: string, deviceId: string }, { rejectWithValue }) => {
    try {
      const response = await authService.validateOtp(otp, deviceId);
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Código OTP inválido');
      }
      
      return true;
    } catch (error: any) {
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const trustDevice = createAsyncThunk(
  'auth/trustDevice',
  async ({ deviceId, trustDevice }: { deviceId: string, trustDevice: string }, { rejectWithValue }) => {
    try {
      const response = await authService.trustDevice(deviceId, trustDevice);
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Error al confiar en el dispositivo');
      }
      
      return trustDevice === "1";
    } catch (error: any) {
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.logout();
      
      // Eliminar token de localStorage independientemente de la respuesta del servidor
      localStorage.removeItem('token');
      
      return true;
    } catch (error: any) {
      // Eliminar token de localStorage aunque haya error
      localStorage.removeItem('token');
      
      if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    resetOtpState: (state) => {
      state.requireOtp = false;
      state.requireTrustDevice = false;
      state.currentDeviceId = null;
      state.otpVerified = false;
    },
    // Para compatibilidad con el middleware
    setLogout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.requireOtp = false;
      state.requireTrustDevice = false;
      state.currentDeviceId = null;
      state.otpVerified = false;
    }
  },
  extraReducers: (builder) => {
    // Obtener perfil de usuario
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      // Si el payload tiene datos, actualizamos el usuario. Si no, mantenemos el usuario actual
      if (action.payload) {
        state.user = action.payload;
        state.isAuthenticated = true;
      }
      // Si no hay datos pero la petición fue exitosa, mantenemos el usuario actual
      // Esto ocurre cuando usamos datos del almacenamiento local
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
      // Si hay un error al obtener el perfil, probablemente el token es inválido
      if (action.payload === 'Sesión expirada o inválida') {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      }
    });
    
    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      if (action.payload) {
        // Guardamos el usuario completo incluyendo todos los campos de la API
        const { token, ...userData } = action.payload;
        state.user = userData;
        state.token = token;
        
        // Guardar en localStorage por seguridad (aunque ya lo hacemos en la acción)
        if (token) {
          localStorage.setItem('token', token);
        }
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Send OTP
    builder.addCase(sendOtp.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(sendOtp.fulfilled, (state, action) => {
      state.isLoading = false;
      state.requireOtp = action.payload.requireOtp;
      state.requireTrustDevice = action.payload.requireTrustDevice;
      state.currentDeviceId = action.payload.currentDeviceId;
    });
    builder.addCase(sendOtp.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Validate OTP
    builder.addCase(validateOtp.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(validateOtp.fulfilled, (state) => {
      state.isLoading = false;
      state.otpVerified = true;
    });
    builder.addCase(validateOtp.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Trust Device
    builder.addCase(trustDevice.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(trustDevice.fulfilled, (state) => {
      state.isLoading = false;
      state.requireTrustDevice = false;
    });
    builder.addCase(trustDevice.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Logout
    builder.addCase(logout.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.requireOtp = false;
      state.requireTrustDevice = false;
      state.currentDeviceId = null;
      state.otpVerified = false;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, setUser, resetOtpState, setLogout } = authSlice.actions;

export default authSlice.reducer;
